import { supabase } from '@/lib/supabase'

export interface ApiKeyRow {
  id: string
  key_prefix: string
  name: string
  created_at: string
  last_used_at: string | null
  expires_at: string | null
  revoked_at: string | null
  organization_id: string | null
  created_by: string | null
}

export interface GeneratedKey {
  id: string
  key_prefix: string
  plaintext_key: string
}

/**
 * List API keys, newest first.
 *
 * - `organizationId = string` → just that org's keys. Use this when the
 *   user has explicitly scoped the view (or has a single org).
 * - `organizationId = null` → all keys across every org the user belongs
 *   to. RLS enforces "must be a member"; the absence of an org filter
 *   means we lean on RLS as the authorization axis. Used by the
 *   "All organizations" view on /account/keys.
 *
 * The PR #10 review flagged "filter by active org" as a P1
 * because mixing keys across orgs while KPI charts are single-org
 * produces inconsistent dashboards. We honor that by keeping KPI cards
 * tied to the picked org and hiding them in the "All" view (the key
 * list itself carries the org name per row so attribution is always
 * unambiguous).
 */
export async function listMyApiKeys(organizationId: string | null): Promise<ApiKeyRow[]> {
  let query = supabase
    .from('api_keys')
    .select(
      'id, key_prefix, name, created_at, last_used_at, expires_at, revoked_at, organization_id, created_by',
    )
    .order('created_at', { ascending: false })
  if (organizationId) {
    query = query.eq('organization_id', organizationId)
  }
  const { data, error } = await query
  if (error) throw new Error(error.message)
  return (data ?? []) as ApiKeyRow[]
}

/**
 * Generate a new API key for the caller's active organization. Server
 * returns the plaintext exactly once — caller MUST display + persist it
 * immediately. The database stores only the SHA-256 hash from this point
 * on. Pass `expiresAt` as an ISO timestamp string (or null for no expiry).
 */
export async function generateMyApiKey(
  name: string,
  expiresAt: string | null = null,
  organizationId: string | null = null,
): Promise<GeneratedKey> {
  const { data, error } = await supabase.rpc('generate_my_api_key', {
    p_name: name,
    p_expires_at: expiresAt,
    // Pass null to fall back to the user's active_organization_id
    // (the RPC's own default). Pass a uuid to mint into a specific org.
    ...(organizationId ? { p_organization_id: organizationId } : {}),
  })
  if (error) throw new Error(error.message)
  const row = Array.isArray(data) ? data[0] : data
  if (!row) throw new Error('No key returned from generate_my_api_key')
  return {
    id: row.id as string,
    key_prefix: row.key_prefix as string,
    plaintext_key: row.plaintext_key as string,
  }
}

/** Revoke a key the signed-in user owns. Returns true if a row was updated. */
export async function revokeMyApiKey(id: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('revoke_my_api_key', { p_key_id: id })
  if (error) throw new Error(error.message)
  return data === true
}
